import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { keys } from '@/lib/queryKeys';
import {
  cancelTransfer, fetchTransfer, fetchTransferStock, fetchTransferStores, fetchTransfers, issueTransfer, receiveTransfer,
} from './api';
import type { IssueTransferInput, ReceiveTransferInput, TransferFilter } from './types';

export function useTransfers(status: TransferFilter, storeId?: number) {
  const { branchVersion } = useAuth();
  return useQuery({
    queryKey: [...keys.pharmacy.transfersList({ status, storeId }), branchVersion],
    queryFn: () => fetchTransfers(status, storeId),
  });
}

export function useTransfer(id: number | null) {
  const { branchVersion } = useAuth();
  return useQuery({
    queryKey: [...keys.pharmacy.transferDetail(id ?? 0), branchVersion],
    queryFn: () => fetchTransfer(id as number),
    enabled: id != null,
  });
}

export function useTransferStores() {
  const { branchVersion } = useAuth();
  return useQuery({ queryKey: [...keys.pharmacy.transferStores(), branchVersion], queryFn: fetchTransferStores });
}

export function useTransferStock(storeId: number | null, q: string, medicineIds?: number[]) {
  const { branchVersion } = useAuth();
  return useQuery({
    queryKey: [...keys.pharmacy.transferStock(storeId ?? 0, q), medicineIds?.join(',') ?? null, branchVersion],
    queryFn: () => fetchTransferStock(storeId as number, q, medicineIds),
    enabled: storeId != null && (medicineIds === undefined || medicineIds.length > 0),
  });
}

/** A transfer moves stock — refresh transfers, inventory everywhere, store totals and the bell. */
function useInvalidateAfterMove() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: keys.pharmacy.transfersAll() });
    qc.invalidateQueries({ queryKey: keys.pharmacy.inventory() });
    qc.invalidateQueries({ queryKey: keys.pharmacy.stores() });
    qc.invalidateQueries({ queryKey: keys.notifications.feed() });
  };
}

export function useIssueTransfer() {
  const after = useInvalidateAfterMove();
  return useMutation({ mutationFn: (input: IssueTransferInput) => issueTransfer(input), onSuccess: after });
}

export function useReceiveTransfer() {
  const after = useInvalidateAfterMove();
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: ReceiveTransferInput }) => receiveTransfer(id, input),
    onSuccess: after,
  });
}

export function useCancelTransfer() {
  const after = useInvalidateAfterMove();
  return useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) => cancelTransfer(id, reason),
    onSuccess: after,
  });
}
